import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, Sun, Globe, LogOut, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { chooseUiLanguage } from "@/lib/uiLang";
import { LangPill } from "@/components/LangPill";
import { ContactAdminButton } from "@/components/ContactAdminDialog";

export function Logo() {
  return (
    <Link
      to="/"
      data-testid="logo-home-link"
      className="flex items-center gap-2 group"
    >
      <div className="relative h-9 w-9 rounded-xl qrise-gradient flex items-center justify-center shadow-lg shadow-purple-500/20">
        <Sparkles className="h-4 w-4 text-white" strokeWidth={2.5} />
      </div>
      <span className="font-semibold text-lg tracking-tight">QRISE</span>
    </Link>
  );
}

export function LanguageSwitch() {
  const { i18n } = useTranslation();
  const { user, setUser } = useAuth() || {};
  const cur = i18n.language?.startsWith("fr") ? "fr" : "en";
  const switchTo = (lang) => chooseUiLanguage(lang, user, setUser);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-xs font-medium"
          data-testid="language-switch-trigger"
        >
          <Globe className="h-3.5 w-3.5" />
          {cur.toUpperCase()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-28">
        <DropdownMenuItem
          onClick={() => switchTo("en")}
          data-testid="language-switch-en"
        >
          English
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => switchTo("fr")}
          data-testid="language-switch-fr"
        >
          Français
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      data-testid="theme-toggle-btn"
      aria-label="Toggle theme"
      className="h-9 w-9"
    >
      {theme === "dark" ? (
        <Sun className="h-4 w-4" />
      ) : (
        <Moon className="h-4 w-4" />
      )}
    </Button>
  );
}

export default function Navbar({ variant = "public" }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      className={
        variant === "public"
          ? "sticky top-0 z-40 backdrop-blur-xl bg-background/70 border-b border-border/60"
          : "sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border/60"
      }
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <Logo />
          {variant === "public" && (
            <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
              <a
                href="#features"
                className="hover:text-foreground transition-colors"
                data-testid="nav-features-link"
              >
                {t("nav.features")}
              </a>
              <a
                href="#games"
                className="hover:text-foreground transition-colors"
                data-testid="nav-games-link"
              >
                {t("nav.games")}
              </a>
              <a
                href="#pricing"
                className="hover:text-foreground transition-colors"
                data-testid="nav-pricing-link"
              >
                {t("nav.pricing")}
              </a>
            </nav>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {variant !== "public" && user?.role === "merchant" ? (
            <>
              <ContactAdminButton />
              <LangPill className="border-border bg-muted/40 mr-1" idPrefix="merchant-lang" />
            </>
          ) : (
            <LanguageSwitch />
          )}
          <ThemeToggle />
          {user ? (
            <>
              {variant === "public" && (
                <Button
                  onClick={() =>
                    navigate(user.role === "admin" ? "/admin" : "/merchant")
                  }
                  size="sm"
                  className="rounded-full ml-1"
                  data-testid="nav-dashboard-btn"
                >
                  {t("nav.dashboard")}
                </Button>
              )}
              {variant !== "public" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  data-testid="nav-logout-btn"
                  className="gap-1.5"
                >
                  <LogOut className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t("nav.logout")}</span>
                </Button>
              )}
            </>
          ) : (
            <Button
              onClick={() => navigate("/login")}
              size="sm"
              className="rounded-full ml-1 bg-foreground text-background hover:bg-foreground/90"
              data-testid="nav-login-btn"
            >
              {t("nav.login")}
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
